import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  Outlet,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { meQuery } from './lib/auth.js';
import { AppShell } from './components/AppShell.js';
import { LoginPage } from './pages/LoginPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { ContactsPage } from './pages/ContactsPage.js';
import { ContactDetailPage } from './pages/ContactDetailPage.js';
import { CompaniesPage } from './pages/CompaniesPage.js';
import { CompanyDetailPage } from './pages/CompanyDetailPage.js';
import { ImportPage } from './pages/ImportPage.js';
import { DuplicatesPage } from './pages/DuplicatesPage.js';
import { SuppressionsPage } from './pages/SuppressionsPage.js';
import { SegmentsPage } from './pages/SegmentsPage.js';
import { SegmentBuilderPage } from './pages/SegmentBuilderPage.js';
import { CampaignsPage } from './pages/CampaignsPage.js';
import { CampaignDetailPage } from './pages/CampaignDetailPage.js';
import { CampaignEditorPage } from './pages/CampaignEditorPage.js';
import { JourneysPage } from './pages/JourneysPage.js';
import { JourneyDetailPage } from './pages/JourneyDetailPage.js';
import { CrmBoardPage } from './pages/CrmBoardPage.js';
import { DealDetailPage } from './pages/DealDetailPage.js';
import { TasksPage } from './pages/TasksPage.js';
import { ActionStreamPage } from './pages/ActionStreamPage.js';
import { ReviewCasesPage } from './pages/ReviewCasesPage.js';
import { AnalyticsPage } from './pages/AnalyticsPage.js';
import { DeliverabilityPage } from './pages/DeliverabilityPage.js';
import { InboxPage } from './pages/InboxPage.js';
import { QueuePage } from './pages/QueuePage.js';
import { ErrorPage } from './pages/ErrorPage.js';

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  errorComponent: ErrorPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData(meQuery);
    if (user) throw redirect({ to: '/' });
  },
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: AppShell,
  beforeLoad: async ({ context, location }) => {
    const user = await context.queryClient.ensureQueryData(meQuery);
    if (!user) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    return { user };
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: DashboardPage,
});

const contactsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/kontakte',
  component: ContactsPage,
});

const importRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/kontakte/import',
  component: ImportPage,
});

const suppressionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/kontakte/sperrliste',
  component: SuppressionsPage,
});

const duplicatesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/kontakte/dubletten',
  component: DuplicatesPage,
});

const contactDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/kontakte/$id',
  component: ContactDetailPage,
});

const segmentsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/segmente',
  component: SegmentsPage,
});

const segmentNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/segmente/neu',
  component: () => <SegmentBuilderPage mode="create" />,
});

const segmentEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/segmente/$id',
  component: () => <SegmentBuilderPage mode="edit" />,
});

const campaignsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/kampagnen',
  component: CampaignsPage,
});

const campaignNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/kampagnen/neu',
  component: CampaignEditorPage,
});

/*
 * Die Einzelkampagne ist keine eigene Seite mehr, sondern dieselbe Ansicht mit
 * einer Auswahl. Die Adresse bleibt erhalten, damit ein Link auf eine
 * bestimmte Kampagne weiterhin funktioniert — er oeffnet jetzt die Liste mit
 * geoeffneter Akte statt einer Seite ohne Umgebung.
 */
const campaignDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/kampagnen/$id',
  component: CampaignDetailPage,
});

const campaignEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/kampagnen/$id/bearbeiten',
  component: CampaignEditorPage,
});

const journeysRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/journeys',
  component: JourneysPage,
});

const journeyDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/journeys/$id',
  component: JourneyDetailPage,
});

const crmRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/crm',
  component: CrmBoardPage,
});

const tasksRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/crm/aufgaben',
  component: TasksPage,
});

const actionStreamRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/crm/action-stream',
  component: ActionStreamPage,
});

const reviewCasesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/crm/review-cases',
  component: ReviewCasesPage,
});

const dealDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/crm/$id',
  component: DealDetailPage,
});

const analyticsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/auswertung',
  component: AnalyticsPage,
});

const deliverabilityRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/zustellbarkeit',
  component: DeliverabilityPage,
});

const inboxRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/inbox',
  component: InboxPage,
});

const queueRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/warteschlange',
  component: QueuePage,
});

const companiesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/firmen',
  component: CompaniesPage,
});

const companyDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/firmen/$id',
  component: CompanyDetailPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    contactsRoute,
    companiesRoute,
    companyDetailRoute,
    importRoute,
    duplicatesRoute,
    suppressionsRoute,
    contactDetailRoute,
    segmentsRoute,
    segmentNewRoute,
    segmentEditRoute,
    campaignsRoute,
    campaignNewRoute,
    campaignDetailRoute,
    campaignEditRoute,
    journeysRoute,
    journeyDetailRoute,
    crmRoute,
    actionStreamRoute,
    reviewCasesRoute,
    tasksRoute,
    dealDetailRoute,
    analyticsRoute,
    deliverabilityRoute,
    inboxRoute,
    queueRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPendingMs: 300,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
